import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import Logo from './Logo';
import { NAV_BY_ROLE, CTA_BY_ROLE, LOGIN_URL } from '../lib/mock';
import { useRole } from '../context/RoleContext';
import MoreMenu from './MoreMenu';

function inferRole(pathname) {
  if (pathname.startsWith('/school')) return 'school';
  if (pathname.startsWith('/student-parent') || pathname.startsWith('/access-request')) return 'student-parent';
  if (pathname.startsWith('/researcher') || pathname.startsWith('/collaboration-request')) return 'researcher';
  if (pathname.startsWith('/coaching') || pathname.startsWith('/adoption-request')) return 'coaching';
  if (pathname.startsWith('/partners') || pathname.startsWith('/partnership-request')) return 'partners';
  if (pathname.startsWith('/explore')) return 'explore';
  return null;
}

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { role, setRole } = useRole();
  const [moreOpen, setMoreOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // sync role from URL
  useEffect(() => {
    const inferred = inferRole(location.pathname);
    if (inferred && inferred !== role) setRole(inferred);
  }, [location.pathname]); // eslint-disable-line

  const isHome = location.pathname === '/';
  const activeRole = isHome ? 'default' : (role || 'default');
  const navItems = NAV_BY_ROLE[activeRole] || NAV_BY_ROLE.default;
  const cta = CTA_BY_ROLE[activeRole] || CTA_BY_ROLE.default;

  const handleNavClick = (item) => {
    setMobileOpen(false);
    if (item.to.includes('#')) {
      const [path, hash] = item.to.split('#');
      if (path && path !== location.pathname) {
        navigate(item.to);
        setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' }), 300);
      } else {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(item.to);
    }
  };

  return (
    <header className="sticky top-0 z-40 border-b border-canvas-200/70 bg-white/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-[1240px] items-center justify-between gap-4 px-6">
        <Logo />

        {!isHome && (
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const [itemPath] = item.to.split('#');
              const active = location.pathname === itemPath;
              return (
                <button
                  key={item.label}
                  onClick={() => handleNavClick(item)}
                  className={`rounded-md px-3 py-2 text-[13.5px] transition-colors ${active ? 'text-navy font-extrabold' : 'font-medium text-ink-500 hover:text-navy hover:bg-canvas-200/60'}`}
                >{item.label}</button>
              );
            })}
            <button
              onClick={() => setMoreOpen(true)}
              className="ml-1 inline-flex items-center gap-1 rounded-md px-3 py-2 text-[13.5px] font-medium text-ink-500 hover:text-navy hover:bg-canvas-200/60 transition-colors"
            >More <ChevronDown className="h-3.5 w-3.5" /></button>
          </nav>
        )}

        <div className="hidden md:flex items-center gap-2">
          {isHome ? (
            <a href={LOGIN_URL} target="_blank" rel="noopener noreferrer" data-testid="header-login-link" className="rounded-lg border border-canvas-200 bg-white px-4 py-2 text-[13.5px] font-semibold text-navy hover:bg-canvas-100 transition-colors">Login</a>
          ) : (
            <>
              {cta.secondary && (
                cta.secondary.label === 'Login'
                  ? <a href={LOGIN_URL} target="_blank" rel="noopener noreferrer" data-testid="header-login-link" className="rounded-lg border border-canvas-200 bg-white px-4 py-2 text-[13.5px] font-semibold text-navy hover:bg-canvas-100 transition-colors">Login</a>
                  : <Link to={cta.secondary.to} className="rounded-lg border border-canvas-200 bg-white px-4 py-2 text-[13.5px] font-semibold text-navy hover:bg-canvas-100 transition-colors">{cta.secondary.label}</Link>
              )}
              <Link to={cta.primary.to} className="rounded-lg bg-brand-gradient px-4 py-2 text-[13.5px] font-semibold text-white shadow-soft hover:brightness-110 transition-all">{cta.primary.label}</Link>
            </>
          )}
        </div>

        <button className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-canvas-200" onClick={() => setMobileOpen(o => !o)} aria-label="menu">
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-canvas-200 bg-white px-6 py-4">
          {!isHome && navItems.map(item => (
            <button key={item.label} onClick={() => handleNavClick(item)} className="block w-full text-left py-2 text-[15px] font-semibold text-navy">{item.label}</button>
          ))}
          <button onClick={() => { setMobileOpen(false); setMoreOpen(true); }} className="block w-full text-left py-2 text-[15px] font-semibold text-navy">More</button>
          <div className="mt-3 flex gap-2">
            {cta.secondary && (
              cta.secondary.label === 'Login'
                ? <a href={LOGIN_URL} target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)} className="rounded-lg border border-canvas-200 bg-white px-4 py-2 text-[13.5px] font-semibold text-navy">Login</a>
                : <Link to={cta.secondary.to} onClick={() => setMobileOpen(false)} className="rounded-lg border border-canvas-200 bg-white px-4 py-2 text-[13.5px] font-semibold text-navy">{cta.secondary.label}</Link>
            )}
            <Link to={cta.primary.to} onClick={() => setMobileOpen(false)} className="rounded-lg bg-brand-gradient px-4 py-2 text-[13.5px] font-semibold text-white shadow-soft">{cta.primary.label}</Link>
          </div>
        </div>
      )}

      <MoreMenu open={moreOpen} onClose={() => setMoreOpen(false)} />
    </header>
  );
}
